import { Injectable } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { Platform } from '@ionic/angular';
import HomeLauncher from 'src/plugins/homeLauncher';
import {
  isLauncherOnboardingSkipped,
  migrateLauncherOnboardingPolicy,
} from 'src/app/shared/launcher-onboarding-policy';
import { DeviceService } from 'src/app/services/device-service/device.service';
import { EnvironmentConfigService } from 'src/app/services/environment-config-service/environment-config-service';

@Injectable({ providedIn: 'root' })
export class StartupGuard {
  constructor(
    private router: Router,
    private platform: Platform,
    private deviceService: DeviceService,
    private environmentConfigService: EnvironmentConfigService,
  ) {}

  async canActivate(): Promise<UrlTree> {
    // ── Web / iOS: skip launcher and license checks ──────────────────────────
    if (!Capacitor.isNativePlatform() || !this.platform.is('android')) {
      return this.router.parseUrl('/login');
    }

    // ── Step 1: One-time policy migration ───────────────────────────────────
    await migrateLauncherOnboardingPolicy();

    // ── Step 2: Default-launcher check ──────────────────────────────────────
    const isDefault = await this.isDefaultLauncher();
    const skipped = await isLauncherOnboardingSkipped();

    if (!isDefault && !skipped) {
      return this.router.parseUrl('/onboarding');
    }

    // ── Step 3: Internet reachability check ─────────────────────────────────
    const canReachInternet = await this.environmentConfigService.CanReachInternet();
    if (!canReachInternet) {
      return this.router.parseUrl('/startup-connectivity');
    }

    // ── Step 4: Device-license check ────────────────────────────────────────
    const hasLicense = await this.hasValidLicense();

    if (!hasLicense) {
      return this.router.parseUrl('/configureLicenseCode');
    }

    return this.router.parseUrl('/landing');
  }

  /** Returns true if this app is the default home launcher. Fails silently → false. */
  private async isDefaultLauncher(): Promise<boolean> {
    try {
      const deadline = new Promise<never>((_, reject) =>
        window.setTimeout(() => reject(new Error('launcher-check-timeout')), 5000),
      );
      const { value } = await Promise.race([HomeLauncher.isDefaultHome(), deadline]);
      return value;
    } catch {
      return false;
    }
  }

  /** Returns true if a valid device license code is stored. Fails silently → false. */
  private async hasValidLicense(): Promise<boolean> {
    try {
      return await this.deviceService.checkDeviceLicenseCodeValidity();
    } catch {
      return false;
    }
  }
}
